import { Button, Icons, useToggle } from '@commenda/kakashi';
import { downloadJsonFile } from '@commenda/utils/downloadFile';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus } from '../../../utils';
import { IndLLPIncorporationTasks } from '../../IN_LLP/ind-llp';
import FormDialog from './Dialog';
import GetNameReservationForm from './forms/GetNameReservationForm';

export const FillNameReservationDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate, workflowExecution } = context;

  const nameReservationModalVisibility = useToggle();

  const { setNotification } = useNotification();

  // A workflow-driven request has no WorkRequest — the questionnaire step carries the names.
  function proposedNamesFromQuestionnaire() {
    const stepExecutions = workflowExecution?.runtime?.stepExecutions;
    if (!stepExecutions) return null;

    const response = Object.values(stepExecutions)
      .map(
        (execution) =>
          (
            execution as {
              outputData?: { formResponse?: Record<string, unknown> };
            }
          ).outputData?.formResponse
      )
      .find((candidate) => typeof candidate?.proposedName1 === 'string');

    if (!response) return null;

    return {
      proposedName1: response.proposedName1 as string,
      proposedName2:
        typeof response.proposedName2 === 'string' ? response.proposedName2 : '',
    };
  }

  function downloadRUNLLPFile() {
    const fillCorporationTask = serviceRequest.taskGroups
      .flatMap((taskGroup) => taskGroup.tasks)
      .find(
        (t) =>
          t.legacyServiceRequestTaskTaskId ===
          IndLLPIncorporationTasks.FILL_CORPORATION_DETAILS
      );

    const requestData = fillCorporationTask?.workRequest?.requestData as
      | { companyLegalName: string; companyLegalName2: string }
      | undefined;

    const proposedNames = requestData
      ? {
          proposedName1: requestData.companyLegalName || '',
          proposedName2: requestData.companyLegalName2 || '',
        }
      : proposedNamesFromQuestionnaire();

    if (!proposedNames) {
      setNotification({
        show: true,
        valid: false,
        title: 'Export not available',
        message: 'Enter the proposed names directly in the MCA RUN form.',
      });
      return;
    }

    const RunLLPJson = {
      version: 1,
      formCode: 'runllp',
      values: {
        purposeOfLLP: 'New Incorporation',
        proposedName1: proposedNames.proposedName1,
        proposedName2: proposedNames.proposedName2,
        nicCodes: [], // TODO: Get actual NIC codes
        attachment: null,
      },
      $schema: 'http://json-schema.org/draft-07/schema#',
    };

    downloadJsonFile(
      `${serviceRequest.company.companyDisplayName}.runllp.json`,
      RunLLPJson
    );
  }

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => downloadRUNLLPFile()}
                >
                  <Icons.Download01 className="w-4" /> Download .runllp.json
                  file
                </Button>
                <Button
                  size="sm"
                  onClick={nameReservationModalVisibility.show}
                  variant="primary"
                >
                  <Icons.Check className="w-4" /> Update name & mark as filed
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Complete MCA Name Reservation (RUN)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={nameReservationModalVisibility.show}
              >
                <Icons.Check className="w-4" /> Update name & mark as filed
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="MCA Name Reservation (RUN) completed"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Agent completes MCA Name Reservation (RUN)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="MCA Name Reservation (RUN) completed"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        // Admin viewers run these steps too, and an unmapped role renders no actions.
        stateMap[state.role === TaskParty.ADMIN ? TaskParty.AGENT : state.role]?.[
          state.status
        ] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <FormDialog
        isOpen={nameReservationModalVisibility.visible}
        setOpen={nameReservationModalVisibility.hide}
        FormComponent={GetNameReservationForm}
        formProps={{
          corporationId: serviceRequest?.corporationId,
          companyLegalName: serviceRequest?.corporation?.companyLegalName,
          setOpen: nameReservationModalVisibility.hide,
          taskID: task.id,
          taskGroupID: task.taskGroupId,
          onSuccessCallback: async () => {
            if (task.status !== TaskStatus.COMPLETED)
              await updateTaskStatus({
                task,
                status: TaskStatus.COMPLETED,
                mutate,
              });
            else await mutate();
          },
        }}
      />
    </div>
  );
};
